import { datadogRum } from '@datadog/browser-rum'
import { get, isInteger } from 'lodash'
import { PRODUCT_VARIANT_STATUSES } from 'constants/common'
import { ROUTE } from 'constants/routes'
import { prepareBreadcrumbsProps } from 'routes/utils/prepareGetStaticBreadcrumbsProps'
import { prepareNavigationProps } from 'routes/utils/prepareGetStaticNavigationProps'
import { PublicProductDocument as PUBLIC_PRODUCT_QUERY } from 'types/graphql-generated'
import { initializeApollo } from 'utils/apolloClient'
import {
  getProductIdFromSlug,
  getProductVariantSlug,
  getVariantIdFromSlug,
  sortProductVariantsByPriceAndQuantity,
} from './utils'

export const prepareGetStaticProps = async (
  context,
  { productSlug, variantSlug, slug }
) => {
  try {
    const apolloClient = initializeApollo()
    const navigation = await prepareNavigationProps()
    const id = getProductIdFromSlug(productSlug)
    if (!isInteger(id)) {
      throw new Error('Invalid identifier')
    }
    const { data } = await apolloClient.query({
      query: PUBLIC_PRODUCT_QUERY,
      variables: {
        productInput: {
          id,
        },
        variantStatuses: [
          PRODUCT_VARIANT_STATUSES.PUBLISHED,
          PRODUCT_VARIANT_STATUSES.NOT_PUBLISHED,
          PRODUCT_VARIANT_STATUSES.ARCHIVED,
        ],
      },
    })

    const destination = get(data, ['product', 'slug'])
    if (!destination) {
      throw new Error('Product not found')
    }

    const forceRedirectToProduct = get(data, [
      'product',
      'forceRedirectToProduct',
    ])

    if (forceRedirectToProduct?.slug) {
      return {
        redirect: {
          destination: ROUTE.SHOWROOM_PRODUCT_DETAIL({
            slug: forceRedirectToProduct.slug,
          }),
          permanent: false,
        },
      }
    }

    const productVariants = get(data, ['product', 'variants'], [])

    const getSelectedVariant = () => {
      if (variantSlug) {
        const variantFromSlug = productVariants.find(
          ({ id: variantId }) => variantId === getVariantIdFromSlug(variantSlug)
        )
        return variantFromSlug
      }

      const variantWithLeadImage = productVariants.find(
        ({ leadImage }) => leadImage !== null
      )
      if (
        variantWithLeadImage &&
        variantWithLeadImage?.status === PRODUCT_VARIANT_STATUSES.PUBLISHED
      ) {
        // return array as method is returning array and in usage is picked 0 index
        return variantWithLeadImage
      }

      const sortedVariants =
        sortProductVariantsByPriceAndQuantity(productVariants)

      const sortedByStatus = sortedVariants.sort((a, b) => {
        // Prioritize PUBLISHED with quantity > 0
        if (
          a.status === 'PUBLISHED' &&
          a.quantity > 0 &&
          (b.status !== 'PUBLISHED' || b.quantity === 0)
        ) {
          return -1
        }
        if (
          b.status === 'PUBLISHED' &&
          b.quantity > 0 &&
          (a.status !== 'PUBLISHED' || a.quantity === 0)
        ) {
          return 1
        }

        // Prioritize PUBLISHED (regardless of quantity)
        if (a.status === 'PUBLISHED' && b.status !== 'PUBLISHED') {
          return -1
        }
        if (b.status === 'PUBLISHED' && a.status !== 'PUBLISHED') {
          return 1
        }

        return 0 // Keep original order for other cases
      })

      return sortedByStatus[0]
    }

    const selectedVariant = getSelectedVariant()

    if (productSlug !== destination || selectedVariant?.slug !== variantSlug) {
      return {
        redirect: {
          destination: ROUTE.SHOWROOM_COLLECTION_PRODUCT_DETAIL({
            slug,
            pid: destination,
            variantSlug: getProductVariantSlug(selectedVariant),
          }),
          permanent: false,
        },
      }
    }

    const breadcrumbs = await prepareBreadcrumbsProps(
      apolloClient,
      ROUTE.SHOWROOM_PRODUCT_DETAIL({ slug: productSlug, variantSlug })
    )

    return {
      props: {
        key: id,
        id,
        navigation,
        breadcrumbs,
        // TODO fix initialApolloState after getDataFromTree implementation #1545
        initialApolloState: apolloClient.cache.extract(),
      },
    }
  } catch (error) {
    datadogRum.addError(error)
    return {
      notFound: true,
      props: { id: null, navigation: [], breadcrumbs: [] },
    }
  }
}
